import { EngineResult } from './constants'
import { decision } from './decision'
import { isDateInSeason } from './isDateInSeason'


// ============================================================
// LOCAL DATE HELPER
// ============================================================
//
// Date-only database values such as YYYY-MM-DD must be
// interpreted as LOCAL calendar dates, not UTC dates.
// ============================================================

function toLocalDate(value) {

  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate()
    )
  }

  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {

    const [
      year,
      month,
      day
    ] = value
      .split('-')
      .map(Number)

    return new Date(
      year,
      month - 1,
      day
    )
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate()
  )
}


// ============================================================
// FORMAT LOCAL DATE
// ============================================================

function formatLocalDate(date) {

  if (!date) {
    return null
  }

  return (
    `${date.getFullYear()}-` +
    `${String(date.getMonth() + 1).padStart(2, '0')}-` +
    `${String(date.getDate()).padStart(2, '0')}`
  )
}


// ============================================================
// GET SEASON START FOR THE SEASON CONTAINING A DATE
// ============================================================
//
// Handles both:
//
//   Normal season
//   Example: Jun 21 → Sep 21
//
// and:
//
//   Wraparound season
//   Example: Dec 21 → Mar 19
//
// For Winter on January 10, 2027,
// the season began December 21, 2026.
// ============================================================

function getSeasonStartDate({
  date,
  season
}) {

  const currentDate = toLocalDate(date)

  if (!currentDate || !season) {
    return null
  }

  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()

  const startMonth = Number(season.start_month)
  const startDay = Number(season.start_day)
  const endMonth = Number(season.end_month)
  const endDay = Number(season.end_day)

  if (
    !startMonth ||
    !startDay ||
    !endMonth ||
    !endDay
  ) {
    return null
  }

  const currentValue =
    month * 100 + day

  const startValue =
    startMonth * 100 + startDay

  const endValue =
    endMonth * 100 + endDay

  let startYear =
    currentDate.getFullYear()

  // ----------------------------------------------------------
  // Wraparound season
  //
  // Example:
  // Winter = Dec 21 → Mar 19
  //
  // January belongs to the Winter that began
  // in the previous calendar year.
  // ----------------------------------------------------------

  if (
    startValue > endValue &&
    currentValue <= endValue
  ) {
    startYear--
  }

  return new Date(
    startYear,
    startMonth - 1,
    startDay
  )
}


// ============================================================
// GET SEASON END FOR THE SEASON CONTAINING A DATE
// ============================================================

function getSeasonEndDate({
  date,
  season
}) {

  const currentDate = toLocalDate(date)

  if (!currentDate || !season) {
    return null
  }

  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()

  const startMonth = Number(season.start_month)
  const startDay = Number(season.start_day)
  const endMonth = Number(season.end_month)
  const endDay = Number(season.end_day)

  if (
    !startMonth ||
    !startDay ||
    !endMonth ||
    !endDay
  ) {
    return null
  }

  const currentValue =
    month * 100 + day

  const startValue =
    startMonth * 100 + startDay

  const endValue =
    endMonth * 100 + endDay

  let endYear =
    currentDate.getFullYear()

  // ----------------------------------------------------------
  // Wraparound season
  //
  // Example:
  // Winter = Dec 21 → Mar 19
  //
  // January belongs to the Winter ending
  // in the same calendar year.
  // ----------------------------------------------------------

  if (
    startValue > endValue &&
    currentValue <= endValue
  ) {
    endYear++
  }

  return new Date(
    endYear,
    endMonth - 1,
    endDay
  )
}


// ============================================================
// EVALUATE ONCE PER SEASON
// ============================================================
//
// Rules:
//
// 1. Once is a Season recurrence rule.
// 2. There is one occurrence per Season.
// 3. The normal annual anchor is the first day of the Season.
// 4. If the template was created after the Season began and
//    this Season has not yet had its occurrence, generate now.
// 5. The next Season returns to the Season's normal start date.
//
// Example:
//
// Tax Season = Jan 15 → Apr 15
//
// Created Feb 28
// → generate Feb 28 for the current Season
//
// Next year
// → generate Jan 15
//
// ============================================================

export function evaluateOncePerSeason({
  template,
  existingTasks = [],
  operationalDate
}) {

  const currentDate =
    toLocalDate(operationalDate)

  if (!currentDate) {
    return decision({
      generate: false,
      code: EngineResult.NOT_READY,
      reason: 'Invalid operational date'
    })
  }

  if (!template.season_id) {
    return decision({
      generate: false,
      code: EngineResult.NOT_READY,
      reason: 'Once per season requires an assigned season'
    })
  }

  const season =
    template.seasons

  if (!season) {
    return decision({
      generate: false,
      code: EngineResult.NOT_READY,
      reason: 'Season data is unavailable'
    })
  }

  if (!season.is_active) {
    return decision({
      generate: false,
      code: EngineResult.NOT_SCHEDULED,
      reason: 'Assigned season is inactive'
    })
  }

  // ----------------------------------------------------------
  // The date being evaluated must actually fall inside
  // the assigned Season.
  // ----------------------------------------------------------

  if (
    !isDateInSeason({
      date: currentDate,
      season
    })
  ) {

    return decision({
      generate: false,
      code: EngineResult.NOT_SCHEDULED,
      reason: `Outside season window: ${season.name}`
    })
  }

  // ----------------------------------------------------------
  // Determine this Season's normal annual anchor.
  // ----------------------------------------------------------

  const seasonStart =
    getSeasonStartDate({
      date: currentDate,
      season
    })

  const seasonEnd =
    getSeasonEndDate({
      date: currentDate,
      season
    })

  if (!seasonStart || !seasonEnd) {
    return decision({
      generate: false,
      code: EngineResult.NOT_READY,
      reason: 'Could not determine season boundaries'
    })
  }

  // ----------------------------------------------------------
  // Determine whether this Season has already produced
  // an occurrence.
  //
  // We look at actual generated tasks, not just the template's
  // dates, because the catch-up rule depends on whether the
  // current Season has already happened.
  // ----------------------------------------------------------

  const existingSeasonTask =
    existingTasks.some(task => {

      if (
        task.recurring_task_id !== template.id ||
        !task.due_date
      ) {
        return false
      }

      const taskDate =
        toLocalDate(task.due_date)

      if (!taskDate) {
        return false
      }

      return (
        taskDate >= seasonStart &&
        taskDate <= seasonEnd
      )
    })

  if (existingSeasonTask) {
    return decision({
      generate: false,
      code: EngineResult.ALREADY_GENERATED,
      reason: 'Season occurrence already exists'
    })
  }

  // ----------------------------------------------------------
  // Determine when this Season's occurrence should happen.
  // ----------------------------------------------------------
  //
  // If the template was created after the Season began,
  // and we are evaluating today/currently during that Season,
  // this is the catch-up occurrence.
  //
  // Otherwise, the normal occurrence is the Season start.
  // ----------------------------------------------------------

  const templateCreated =
    toLocalDate(template.created_at)

  let occurrenceDate =
    seasonStart

  if (
    templateCreated &&
    templateCreated > seasonStart &&
    templateCreated <= currentDate
  ) {
    occurrenceDate = currentDate
  }

  // ----------------------------------------------------------
  // Only generate on the determined occurrence date.
  // ----------------------------------------------------------

  if (
    formatLocalDate(currentDate) !==
    formatLocalDate(occurrenceDate)
  ) {

    return decision({
      generate: false,
      code: EngineResult.NOT_SCHEDULED,
      reason:
        `Once per season scheduled for ${formatLocalDate(occurrenceDate)}`,
      nextGeneration: occurrenceDate
    })
  }

  return decision({
    generate: true,
    code: EngineResult.READY,
    reason: 'Once per season occurrence is due',
    dueDate: occurrenceDate,
    nextGeneration: occurrenceDate
  })
}